import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { comunidadAdapter } from "@/hooks/api/comunidad";
import { toastUtils } from "@/hooks/useToast";
import logoPsi from "@/assets/logoPsiMammoliti.svg";

/** Escala pensada en lo que le aportó la formación (no en "me gustó / no me gustó"). */
export const RATING_LABELS: Record<number, string> = {
  1: "No me aportó",
  2: "Me aportó poco",
  3: "Me aportó algo",
  4: "Me aportó mucho",
  5: "Me enriqueció muchísimo",
};

const COMMENT_MAX = 500;

interface FormacionRatingCardProps {
  formacionKey: string;
  formacionTitle: string;
  /** Ventana de calificación abierta (ver CALIFICA_VISIBLE_FROM/UNTIL en ComunidadContent). */
  open: boolean;
  /** La calificación que ya dejó, si dejó — muestra el "gracias" en vez del formulario. */
  initialRating: { rating: number; comment: string } | null;
}

/**
 * Tarjeta "Calificá" de Comunidad — solo se monta para quien participó de la formación. Se
 * califica de 1 a 5 con el isotipo de Psi Mammoliti (simple, para que responda sí o sí) y un
 * comentario breve opcional.
 */
export const FormacionRatingCard = ({ formacionKey, formacionTitle, open, initialRating }: FormacionRatingCardProps) => {
  const [rating, setRating] = useState<number>(initialRating?.rating ?? 0);
  const [hovered, setHovered] = useState<number>(0);
  const [comment, setComment] = useState(initialRating?.comment ?? "");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(!!initialRating);

  const shown = hovered || rating;

  const handleSubmit = async () => {
    if (!rating) return;
    setSending(true);
    try {
      await comunidadAdapter.calificarFormacion(formacionKey, rating, comment.trim());
      setSent(true);
    } catch {
      toastUtils.error("No se pudo enviar tu calificación. Probá de nuevo.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col gap-3.5 rounded-[20px] bg-white p-6 shadow-[0_6px_20px_rgba(109,87,188,0.08)]">
      <div className="flex flex-col gap-1">
        <span className="text-xs font-bold uppercase tracking-wide text-[#8475B3]">Calificá</span>
        <span className="text-[15px] font-semibold leading-snug text-[#2D2049]">
          ¿Cuánto te aportó &ldquo;{formacionTitle}&rdquo;?
        </span>
      </div>

      {!open ? (
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-300">Próximamente</span>
      ) : sent ? (
        <div className="flex flex-col gap-2 rounded-xl bg-[#F5F1FF] p-4">
          <div className="flex items-center gap-1" aria-label={`Tu calificación: ${rating} de 5`}>
            {[1, 2, 3, 4, 5].map((n) => (
              <img
                key={n}
                src={logoPsi}
                alt=""
                className={`h-7 w-7 ${n <= rating ? "" : "opacity-25 grayscale"}`}
              />
            ))}
          </div>
          <span className="text-sm font-semibold text-[#2D2049]">¡Gracias por tu calificación! 💜</span>
          <span className="text-[13px] text-gray-500">Tu feedback llega a quien brindó la formación.</span>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1" role="radiogroup" aria-label="Calificación de 1 a 5" onMouseLeave={() => setHovered(0)}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={rating === n}
                  aria-label={`${n} — ${RATING_LABELS[n]}`}
                  onMouseEnter={() => setHovered(n)}
                  onClick={() => setRating(n)}
                  className="rounded-full p-0.5 transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8475B3]"
                >
                  <img
                    src={logoPsi}
                    alt=""
                    className={`h-9 w-9 transition-opacity ${n <= shown ? "" : "opacity-25 grayscale"}`}
                  />
                </button>
              ))}
            </div>
            <span className="min-h-[18px] text-[13px] font-semibold text-[#665090]">
              {shown ? RATING_LABELS[shown] : "Elegí de 1 a 5"}
            </span>
          </div>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, COMMENT_MAX))}
            placeholder="¿Algún comentario o sugerencia? (opcional)"
            rows={2}
            className="resize-none text-sm"
          />
          <Button
            variant="outline"
            size="sm"
            className="self-start border-[#C9BCE8] text-[#665090]"
            disabled={!rating || sending}
            onClick={handleSubmit}
          >
            {sending ? "Enviando…" : "Enviar calificación"}
          </Button>
        </>
      )}
    </div>
  );
};

export default FormacionRatingCard;
